import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import issueService from '../../services/issueService';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import IssueStore from './IssueStore';
import CommonModal from '../../services/CommonModal';
import { FaFileExcel } from 'react-icons/fa';

const IssueList = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { selectedIssue, setSelectedIssue, clearSelectedIssue } = IssueStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const confirmDelete = () => {
    handleDelete(selectedIssue.id);
    setShowDeleteModal(false);
    setSelectedIssue(null);
  };
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    issue_type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadIssues();
  }, [filters, pagination.page]);

  const loadIssues = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const data = await issueService.getAll(params);
      setIssues(data.issues);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error loading issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
    {
      try {
        await issueService.delete(id);
        loadIssues();
      } catch (error) {
        console.error('Error deleting issue:', error);
        alert('Failed to delete issue');
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/app/issues/${id}/edit`);
    console.log("iddd", id)
  };;


  const clearFilters = () => {
    setFilters({
      status: '',
      severity: '',
      issue_type: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading && issues.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading issues...</div>
      </div>
    );
  }

  const handleDownloadIssueList = () => {
    if (!issues || issues.length === 0) {
      alert('No issues to download');
      return;
    }

    const headers = [
      "Issue Title",
      "Project Name",
      "Issue Type",
      "Severity",
      "Status",
      "Assignee",
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = issues.map((issue) => [
      issue.title,
      issue.project?.name || '',
      issue.issue_type,
      issue.severity,
      issue.status,
      issue.assignee?.name || ''
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `baselines-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Issues</h2>
        <div className="flex items-center space-x-3">
        <Link
          to="/app/issues/create"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + New Issue
        </Link>
        <button
          onClick={handleDownloadIssueList}
          className="px-4 py-2 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
          title="Download issue list"
        >
          <FaFileExcel />
        </button>
        </div>
      </div>
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Issue"
        message="Are you sure you want to delete this issue?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedIssue(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search issues..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              {issueService.getStatuses().map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity
            </label>
            <select
              name="severity"
              value={filters.severity}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Severities</option>
              {issueService.getSeverities().map(severity => (
                <option key={severity} value={severity}>{severity}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              name="issue_type"
              value={filters.issue_type}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              {issueService.getIssueTypes().map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {(filters.status || filters.severity || filters.issue_type || filters.search) && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Issues List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {issues.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No issues found</p>
            <Link
              to="/app/issues/create"
              className="mt-4 inline-block text-blue-600 hover:text-blue-800"
            >
              Create your first issue
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignee
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {issues.map((issue) => (
                  <tr key={issue.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/app/issues/${issue.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {issue.title}
                      </Link>
                      {issue.description && (
                        <p className="text-sm text-gray-500 mt-1 truncate max-w-md">
                          {issue.description}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {issue.project?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg">
                        {issueService.getIssueTypeIcon(issue.issue_type)}
                      </span>
                      <span className="ml-2 text-sm text-gray-900">
                        {issue.issue_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${issueService.getSeverityColor(issue.severity)}`}>
                        {issue.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${issueService.getStatusColor(issue.status)}`}>
                        {issue.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {issue.assignee ? (
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                            {issue.assignee.first_name[0]}{issue.assignee.last_name[0]}
                          </div>
                          <span className="ml-2 text-sm text-gray-900">
                            {issue.assignee.first_name} {issue.assignee.last_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {/* <Link
                        to={`/app/issues/${issue.id}/edit`}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        
                      </Link> */}

                      <button
                        onClick={() => handleEdit(issue.id)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <FiEdit2 />
                      </button>

                      <button
                        onClick={() => {
                          setSelectedIssue(issue);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{' '}
            of <span className="font-medium">{pagination.total}</span> results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IssueList;


